import React, { useState } from 'react';
import { 
  Dropdown, 
  Button, 
  Space, 
  Typography, 
  Tag,
  Modal
} from 'antd';
import { 
  UserOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  BookOutlined,
  LinkOutlined,
  CodeOutlined,
  DownOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useChatUI, ChatUIRole } from '../../contexts/ChatUIContext';

const { Text } = Typography;

/**
 * ChatUIRoleSelector
 * 
 * UX Principle #9: Mode Switching Rules
 * - Execution Mode is always one interaction away
 * - Mode changes must be explicit, visible, reversible
 * - System may suggest mode changes
 * - System must never auto-switch silently
 */
export const ChatUIRoleSelector: React.FC = () => {
  const { uiRole, setUIRole } = useChatUI();
  const [confirmModal, setConfirmModal] = useState<{ visible: boolean; targetRole: ChatUIRole | null }>({ 
    visible: false, 
    targetRole: null 
  });

  const handleRoleChange = (newRole: ChatUIRole) => {
    // UX Principle #9: Mode changes must be explicit
    // Show confirmation when leaving Execution Mode (operator)
    if (uiRole === 'operator' && newRole !== 'operator') {
      setConfirmModal({ visible: true, targetRole: newRole });
    } else {
      setUIRole(newRole);
    }
  };

  const confirmModeChange = () => {
    if (confirmModal.targetRole) {
      setUIRole(confirmModal.targetRole);
    }
    setConfirmModal({ visible: false, targetRole: null });
  };

  const getRoleIcon = (role: ChatUIRole) => {
    switch (role) {
      case 'architect':
        return <SettingOutlined style={{ color: '#1890ff' }} />;
      case 'operator':
        return <PlayCircleOutlined style={{ color: '#52c41a' }} />;
      case 'coach':
        return <BookOutlined style={{ color: '#fa8c16' }} />;
      case 'integrator':
        return <LinkOutlined style={{ color: '#722ed1' }} />;
      case 'development':
        return <CodeOutlined style={{ color: '#13c2c2' }} />;
      case 'marketfit':
        return <UserOutlined style={{ color: '#eb2f96' }} />;
      default:
        return <UserOutlined />;
    }
  };

  const getRoleColor = (role: ChatUIRole) => {
    switch (role) {
      case 'architect':
        return 'blue';
      case 'operator':
        return 'green';
      case 'coach':
        return 'orange';
      case 'integrator':
        return 'purple';
      case 'development':
        return 'cyan';
      case 'marketfit':
        return 'magenta';
      default:
        return 'default';
    }
  };

  // UX Principle #8: Mode-specific descriptions aligned with Exo Panel UX
  const getRoleDescription = (role: ChatUIRole) => {
    switch (role) {
      case 'architect':
        return 'Build or modify the model. No live execution.';
      case 'operator':
        return 'Execution Mode — confirm signals, maintain flow.';
      case 'coach':
        return 'Guidance only. No enforcement, no execution.';
      case 'integrator':
        return 'Technical integration. Correctness over speed.';
      case 'development':
        return 'Hypotheticals allowed. No production commits.';
      case 'marketfit':
        return 'Assess external entropy. No structural changes.';
      default:
        return 'Unknown role';
    }
  };

  // Get mode constraint warnings
  const getModeConstraint = (role: ChatUIRole) => {
    switch (role) {
      case 'architect':
        return 'Cannot issue live execution signals';
      case 'operator':
        return null; // Default mode, no warning
      case 'coach':
        return 'Zero enforcement capability';
      case 'integrator':
        return 'Model mutation limited to integration scope';
      case 'development':
        return 'No production commitments';
      case 'marketfit':
        return 'View only — no execution or changes';
      default:
        return null;
    }
  };

  // Create role menu items - operator (Execution Mode) first per UX Principle #9
  const roleItems: ChatUIRole[] = [
    'operator',  // Execution Mode always first — one interaction away
    'architect',
    'coach',
    'integrator',
    'development',
    'marketfit'
  ];
  
  const menuItems = roleItems.map((role: ChatUIRole): { key: string; label: React.ReactNode; onClick: () => void } => ({
    key: role,
    label: (
      <div style={{ padding: '8px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          {getRoleIcon(role)}
          <Text strong style={{ textTransform: 'capitalize' }}>
            {role === 'operator' ? 'Execution' : role}
          </Text>
          {role === 'operator' && (
            <Tag color="blue" style={{ fontSize: '10px' }}>DEFAULT</Tag>
          )}
          {uiRole === role && (
            <Tag color={getRoleColor(role)}>
              Active
            </Tag>
          )}
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {getRoleDescription(role)}
          </Text>
        </div>
        {/* Show constraint warning for non-operator modes */}
        {getModeConstraint(role) && (
          <div style={{ 
            fontSize: '11px', 
            color: '#faad14', 
            marginTop: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <ExclamationCircleOutlined style={{ fontSize: '10px' }} />
            {getModeConstraint(role)}
          </div>
        )}
      </div>
    ),
    onClick: () => handleRoleChange(role)
  }));

  const menu = {
    items: menuItems,
    style: { minWidth: '280px' }
  };

  return (
    <>
      <Dropdown 
        menu={menu} 
        trigger={['click']}
        placement="bottomLeft"
      >
        <Button 
          type="text"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            width: '100%',
            height: '40px',
            borderRadius: '6px',
            border: `1px solid ${uiRole === 'operator' ? 'rgba(82, 196, 26, 0.5)' : 'rgba(255, 255, 255, 0.2)'}`,
            backgroundColor: uiRole === 'operator' ? 'rgba(82, 196, 26, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = uiRole === 'operator' 
              ? 'rgba(82, 196, 26, 0.2)' 
              : 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = uiRole === 'operator'
              ? 'rgba(82, 196, 26, 0.1)'
              : 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <Space size="small" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space size="small">
              {getRoleIcon(uiRole)}
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '14px', fontWeight: '500', lineHeight: '1.2', color: 'white' }}>
                  {uiRole === 'operator' ? 'Execution' : uiRole.charAt(0).toUpperCase() + uiRole.slice(1)}
                </div>
                <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', lineHeight: '1.2' }}>
                  {uiRole === 'operator' ? 'Mode (Default)' : 'Mode'}
                </div>
              </div>
            </Space>
            <DownOutlined style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }} />
          </Space>
        </Button>
      </Dropdown>

      {/* Quick return to Execution Mode - UX Principle #9 */}
      {uiRole !== 'operator' && (
        <Button
          type="text"
          size="small"
          onClick={() => setUIRole('operator')}
          style={{
            marginTop: '4px',
            width: '100%',
            fontSize: '11px',
            color: '#52c41a',
            backgroundColor: 'rgba(82, 196, 26, 0.1)',
            border: '1px solid rgba(82, 196, 26, 0.3)',
            borderRadius: '4px'
          }}
        >
          ↩ Return to Execution Mode
        </Button>
      )}

      {/* Mode change confirmation modal - UX Principle #9 */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Leave Execution Mode?</span>
          </div>
        }
        open={confirmModal.visible}
        onOk={confirmModeChange}
        onCancel={() => setConfirmModal({ visible: false, targetRole: null })}
        okText={`Switch to ${confirmModal.targetRole ? confirmModal.targetRole.charAt(0).toUpperCase() + confirmModal.targetRole.slice(1) : ''} Mode`}
        cancelText="Stay in Execution Mode"
        width={400}
      >
        <div style={{ padding: '12px 0' }}>
          <p style={{ marginBottom: '12px' }}>
            You are leaving <strong>Execution Mode</strong> (the default operating mode).
          </p>
          {confirmModal.targetRole && getModeConstraint(confirmModal.targetRole) && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: '6px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#d48806' }}>
                <ExclamationCircleOutlined />
                <strong>Mode Constraint:</strong>
              </div>
              <div style={{ marginTop: '8px', color: '#614700' }}>
                {getModeConstraint(confirmModal.targetRole)}
              </div>
            </div>
          )}
          <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
            You can return to Execution Mode at any time.
          </p>
        </div>
      </Modal>
    </>
  );
};

